import { useCallback } from 'react';
import type { Command } from '@atlassian/jira-command-palette';
import { useGetIssueCommand } from '@atlassian/jira-command-palette-common';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { ISSUE_LIMIT } from '../constants';
import { messages } from '../messages';
import type { GetQuickSearchChildResultProps } from '../types';
import { getQuickSearchIssueScope, transformQuickSearchIssueResultToIssue } from '../utils';
import { useGetViewAllCommand } from '../view-all';
import { ViewAllType, getViewAllUrl } from '../view-all/utils';
import { useIssueCommandAction } from './issue-actions';

type UseGetQuickSearchIssueCommands = {
	section: string;
	errorMessage: string;
	showViewAll: boolean;
};

export const useGetQuickSearchIssueCommands = ({
	section,
	showViewAll,
}: UseGetQuickSearchIssueCommands) => {
	const { formatMessage } = useIntl();

	const { getIssueCommand } = useGetIssueCommand();
	const { getViewAllCommand } = useGetViewAllCommand();
	const { getIssueCommandAction } = useIssueCommandAction();

	const getQuickSearchIssueCommands = useCallback(
		({ error, search, quickSearchResponseData }: GetQuickSearchChildResultProps): Command[] => {
			const issueScope = getQuickSearchIssueScope(quickSearchResponseData);

			// No results
			if (!issueScope || error || issueScope?.error || issueScope?.results?.length === 0) {
				return [];
			}

			// There are results!
			const commands: Command[] = issueScope.results.slice(0, ISSUE_LIMIT).map((issue) => {
				const command = getIssueCommand({
					issue: transformQuickSearchIssueResultToIssue(issue),
					keywords: search,
					section,
					analytics: {
						isSearchResult: true,
					},
				});
				if (ff('compal-issue-actions-quick-search_mqfyf')) {
					return {
						...command,
						primaryAction: getIssueCommandAction(transformQuickSearchIssueResultToIssue(issue)),
					};
				}
				return command;
			});

			if (!showViewAll || issueScope.results.length <= ISSUE_LIMIT) {
				return commands;
			}

			const viewAllCommand = getViewAllCommand({
				id: 'view-all-issues',
				keywords: search,
				section,
				name: formatMessage(messages.viewAllResults),
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				url: getViewAllUrl(ViewAllType.ISSUE, search)!,
				analyticsPayload: { action: 'viewAllIssues', size: issueScope.size },
			});

			return commands.concat([viewAllCommand]);
		},
		[
			formatMessage,
			getIssueCommand,
			getIssueCommandAction,
			getViewAllCommand,
			section,
			showViewAll,
		],
	);

	return { getQuickSearchIssueCommands };
};
